import {Dispatch, SetStateAction} from "react";
import {ImageUpload} from "./ImageUpload";
import {getCssVar} from "../../../utils/colorcontrast";
import {ThemeColorInput} from "./ThemeColorInput";
import {runInAction} from "mobx";
import {useStores} from "../../../stores/useStores";

type ITheme = {
    id: number,
    folder: string,
    title: string,
    description: string,
    fields: IThemeField[],
}

type IThemeField = {
    default_value: string,
    name: string,
    title: string,
    field_type: string,
}

type IThemeSettingsProps = {
    themeOptions: ITheme[],
    selectedThemeId: number,
    setSelectedThemeId: Dispatch<SetStateAction<number>>,
    themeFieldSettings: {
        [themeId: number]: {
            [fieldName: string]: string,
        }
    },
    setThemeFieldSettings: Dispatch<SetStateAction<any>>,
    backgroundImage: string,
    setBackgroundImage: Dispatch<SetStateAction<any>>,
}

export function ThemeSettings(props: IThemeSettingsProps) {
    const {organizationStore, styleStore} = useStores();

    const selectedTheme: ITheme = props.themeOptions.find(t => t.id === props.selectedThemeId) || props.themeOptions[0];

    function handleThemeChange(newThemeId) {
        let themeName;
        switch (newThemeId) {
            case 1:
                themeName = "simple_and_clean";
                break;
            case 2:
                themeName = "floating_blocks";
                break;
        }

        const previousTheme = getCssVar('--theme');
        if (previousTheme !== themeName && newThemeId) {
            // we only need to switch body classes when the theme has changed, not on initial render, or it causes a weird flash of the wrong theme
            const wrapper = document.querySelector(`.sb-theme-${previousTheme}`)
            if (wrapper) {
                wrapper.classList.remove(`sb-theme-${previousTheme}`);
                wrapper.classList.add(`sb-theme-${themeName}`);
            }
            styleStore.setCssVars({theme: themeName});
            organizationStore.updateThemeSettings({}, newThemeId);
            props.setSelectedThemeId(newThemeId);
        }
    }

    function handleThemeSettingChange(field, value) {
        if (field === "backgroundImage") {
            runInAction(() => organizationStore.currentOrganization.backgroundImage = value)
            props.setBackgroundImage(value);
        } else {
            props.setThemeFieldSettings(prev => {
                return {
                    ...prev,
                    [props.selectedThemeId]: {
                        ...prev[props.selectedThemeId],
                        [field]: value,
                    },
                }
            })
        }
    }

    return <div className="panel panel-default">
        <div className="panel-heading">
            <h3 className="panel-title text-center">Theme Settings</h3>
        </div>
        <div className="panel-body">
            <div className="row">
                <div className="col-lg-12">
                    For help making the Floating Blocks theme look fantastic, please click <a
                    href="https://docs.google.com/document/d/1sdzUDHx2mBNZPen2jLeSux2iUOZmtN8LYWrFDm3Lz7c/edit?usp=sharing">here</a> to
                    view our guide. To learn more about "Compliance" with Web content accessibility guidelines,
                    click <a
                    href="https://docs.google.com/document/d/1oh4-RkbbF4DPgwEQjLYrbyQoWrIu7Qsf_xN2wDSRdtw/edit?usp=sharing">here</a>
                </div>
            </div>

            <div className="sb-main-theme-selector row">
                <div className="col-lg-3">
                    <label>Theme:</label>
                </div>
                <div className="col-lg-4">
                    <select
                        value={selectedTheme.id}
                        className={'form-control'}
                        title={"Theme List"}
                        onChange={(e) => {
                            const themeID = e.target.value;
                            handleThemeChange(Number(themeID));
                        }}
                    >
                        {props.themeOptions.map(theme => <option key={theme.id} value={Number(theme.id)}>{theme.title}</option>)}
                    </select>
                </div>
            </div>
            <div className="sb-main-themes-holder">
                <div className="sb-theme-fields">
                    {selectedTheme.fields.map(field => {
                        let val = field.default_value;
                        if (props.themeFieldSettings[selectedTheme.id][field.name]) {
                            val = props.themeFieldSettings[selectedTheme.id][field.name];
                        }

                        let content;
                        if (field.field_type === "color") {
                            content = <ThemeColorInput
                                key={field.name}
                                name={field.name}
                                currentThemeId={selectedTheme.id}
                                value={val}
                                onChange={value => handleThemeSettingChange(field.name, value)}
                            />
                        } else if (field.field_type === "input" || field.field_type === "float") {
                            content = <input key={field.name} title={`${field.field_type} input`}
                                             type="text" className="form-control" value={val}
                                             onChange={e => handleThemeSettingChange(field.name, e.target.value)}/>
                        } else if (field.field_type === "real") {
                            content = <input key={field.name} title={`${field.field_type} input`}
                                             type="number" className="form-control" value={val}
                                             onChange={e => handleThemeSettingChange(field.name, e.target.value)}/>
                        } else if (field.field_type === "image") {
                            content = <ImageUpload
                                imageUrl={organizationStore.currentOrganization.backgroundImage}
                                imageType={"backgroundImage"}
                                handleImageUrlChange={value => handleThemeSettingChange(field.name, value)}
                                label={""}
                            />
                        }

                        return <div className="sb-theme-field row" key={field.name+selectedTheme.id}>
                            <div className="col-lg-3">
                                <label>{field.title}:</label>
                            </div>
                            <div className={field.field_type === "image" ? 'col-lg-9' : ''}>
                                {content}
                            </div>
                        </div>
                    })}
                </div>
            </div>
        </div>
    </div>
}
